import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import Checkbox from '@rio-cloud/rio-uikit/Checkbox';
import Button from '@rio-cloud/rio-uikit/components/button/Button';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { CLOSE_DIALOG_EVENT } from '@rio-cloud/rio-uikit/FrameDialog';
import usePostMessageSender from '@rio-cloud/rio-uikit/usePostMessageSender';

import { SETTINGS_SAVED } from '@/features/app/MessageHandler';
import { DialogErrorState } from '@/features/common/components/dialogs/common/DialogErrorState';
import { DialogLoadingState } from '@/features/common/components/dialogs/common/DialogLoadingState';
import {
    MFE_DIALOG_PREFIX,
    TABLE_BRAKE_LINING,
    TABLE_TO_API_MAP,
} from '@/features/common/components/dialogs/dialogConstants';
import { USER_SETTINGS_BROADCAST_CHANNEL, useUserSettings } from '@/features/common/hooks/useUserSettings';
import { BrakeLiningSettings } from '@/generated/GeneratedApiTypes';
import { usePutDialogDataMutation } from '@/services/dialogsApi';

const UserSettingsDialog: React.FC = () => {
    const { diagnosticsTableView } = useParams<{ diagnosticsTableView: string }>();
    const intl = useIntl();
    const { sendMessageToParent } = usePostMessageSender();

    const tableType = diagnosticsTableView || TABLE_BRAKE_LINING;
    const { settings, isSettingsError, refetch: refetchSettings } = useUserSettings<BrakeLiningSettings>(tableType);

    const [settingsData, setSettingsData] = useState<BrakeLiningSettings | null>(settings ?? null);

    const handleCloseDialog = useCallback(() => {
        sendMessageToParent({
            type: `${MFE_DIALOG_PREFIX}${CLOSE_DIALOG_EVENT}`,
            payload: `Closed table settings dialog from ${tableType}`,
        });
    }, [sendMessageToParent, tableType]);

    const [updateSettings, { isLoading: isSaving }] = usePutDialogDataMutation();

    useEffect(() => {
        if (settings) {
            setSettingsData({ ...settings });
        }
    }, [settings]);

    const handleCheckboxChange = useCallback((key: string) => {
        setSettingsData((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                columns: {
                    ...prev.columns,
                    [key]: !(prev.columns as unknown as Record<string, Boolean>)[key],
                },
            };
        });
    }, []);

    const handleSaveDialog = useCallback(async () => {
        if (!settingsData) return;
        const channel = new BroadcastChannel(USER_SETTINGS_BROADCAST_CHANNEL);
        try {
            const path = TABLE_TO_API_MAP[tableType as keyof typeof TABLE_TO_API_MAP];
            const result = (await updateSettings({
                path,
                data: settingsData,
            }).unwrap()) as BrakeLiningSettings;
            channel.postMessage({
                type: SETTINGS_SAVED,
                payload: { tableType, settings: result },
            });
        } catch (error) {
            console.error('Failed to update settings:', error);
        }
        channel.close();
        handleCloseDialog();
    }, [settingsData, updateSettings, handleCloseDialog, tableType]);

    if (isSettingsError) {
        return <DialogErrorState onClose={handleCloseDialog} refetch={refetchSettings as unknown as () => any} />;
    }

    if (!settingsData) {
        return <DialogLoadingState onClose={handleCloseDialog} />;
    }

    return (
        <>
            <Dialog.Title
                title={intl.formatMessage({ id: 'intl-msg:vehicle_diagnostics.table_settings.header' })}
                onCloseButtonClick={handleCloseDialog}
            />

            <Dialog.Body className={'padding-15 '}>
                {settingsData?.columns && (
                    <div className={'margin-left-10'}>
                        <div className={'margin-top-20 margin-bottom-15'}>
                            <strong>
                                {intl.formatMessage({
                                    id: 'intl-msg:vehicle_diagnostics.table_settings.optional.columns',
                                })}
                            </strong>
                        </div>

                        <table className="table-borderless">
                            <tbody>
                                {Object.entries(settingsData.columns).map(([key, value]) => (
                                    <tr key={key}>
                                        <td>
                                            <Checkbox
                                                id={`${key}-checkbox`}
                                                label={intl.formatMessage({
                                                    id: `intl-msg:vehicle_diagnostics.table_settings.column.${key}`,
                                                })}
                                                checked={value}
                                                onChange={() => handleCheckboxChange(key)}
                                                className={'margin-bottom-5'}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Dialog.Body>

            <Dialog.Footer className="d-flex justify-content-end">
                <Button
                    id={'closeTableSettingsDialogButton'}
                    className="btn margin-right-10"
                    onClick={handleCloseDialog}
                >
                    <FormattedMessage id="intl-msg:vehicle-diagnostics.dialog.close" />
                </Button>
                <Button
                    id={'saveTableSettingsDialogButton'}
                    className="btn btn-primary"
                    onClick={handleSaveDialog}
                    disabled={isSaving}
                >
                    <FormattedMessage id="intl-msg:vehicle-diagnostics.dialog.save" />
                </Button>
            </Dialog.Footer>
        </>
    );
};

export default UserSettingsDialog;
